import React from 'react'
import TextField from '@material-ui/core/TextField'
import InputAdornment from '@material-ui/core/InputAdornment'
import { TextFieldProps } from '@material-ui/core/TextField/TextField'
import { WithStyles } from '@material-ui/core/styles/withStyles'
import { Theme, withStyles } from '@material-ui/core/styles'

const styles = (theme: Theme) => ({
  formControl: {
    '.has-left-icon &': {
      left: '33px',

      'html[dir=rtl] &': {
        left: 'auto',
        right: '33px',
      },
    },
    '.has-left-icon2 &': {
      left: '41px',

      'html[dir=rtl] &': {
        left: 'auto',
        right: '41px',
      },
    },
  },
})

interface YumInputProps {
  classes: WithStyles
  startIcon?: any
  startIconInactive?: any
  endIcon?: any
  normalIconClass?: boolean
}

let yumInputIdCounter = 0

class YumInput extends React.Component<YumInputProps & TextFieldProps> {
  // Stable auto-generated id so MUI's InputLabel can bind via htmlFor even when
  // the caller doesn't supply an explicit id — required for axe's `label` rule
  // (WCAG 1.3.1) to see the label/input association.
  private autoId: string
  constructor(props: any) {
    super(props)
    this.state = {
      shrink: false, // this is used to shrink/unshrink the label
    }
    yumInputIdCounter += 1
    this.autoId = `yum-input-${yumInputIdCounter}`
  }

  shrinkLabel = (event: any) => {
    const { onFocus } = this.props
    this.setState({ shrink: true })
    onFocus && onFocus(event) // let the child do its thing
  }

  unShrinkLabel = (event: any) => {
    const { onBlur } = this.props
    if (event.target.value.length === 0 && !event.target.validity.badInput) {
      this.setState({ shrink: false }) // gotta make sure the input is empty before shrinking the label
    }
    onBlur && onBlur(event) // let the child do its thing
  }

  render() {
    // make sure to check endIcon and startIcon, we don't need errors in our console
    const {
      classes,
      startIcon,
      startIconInactive,
      endIcon,
      shrink: propsShrink,
      normalIconClass,
      ...other
    }: any = this.props
    const inputLabelProps = this.props.InputLabelProps || {}
    // Respect an explicit shrink flag from the caller first.
    // This lets higher-level inputs (like phone inputs) force the label state.
    const shrink =
      inputLabelProps.shrink !== undefined
        ? inputLabelProps.shrink
        : // @ts-ignore
          propsShrink || (other.value !== null && other.value !== undefined && other.value !== '') || this.state.shrink

    const StartIcon = startIcon ? startIcon : null
    const StartIconInactive = startIconInactive ? startIconInactive : null
    const EndIcon = endIcon ? endIcon : null

    let classNames = other.className || ''
    if (StartIcon) classNames += ' has-left-icon'
    if (this.props.InputProps?.startAdornment) {
      if (normalIconClass) {
        classNames += ' has-left-icon'
      } else {
        classNames += ' has-left-icon2'
      }
    }

    const inputProps: any = {
      startAdornment:
        this.props.InputProps?.startAdornment ||
        (startIcon && (
          // Decorative leading icon (the field already has a visible label), so
          // hide it from assistive tech instead of announcing an unnamed graphic (WCAG 1.1.1).
          <InputAdornment position={'start'} style={{ marginTop: other.multiline ? 4 : 0 }} aria-hidden="true">
            {!StartIconInactive || shrink ? <StartIcon className="one-icon" /> : <StartIconInactive />}
          </InputAdornment>
        )),
      endAdornment:
        this.props.InputProps?.endAdornment ||
        (endIcon && (
          <InputAdornment position={'end'} aria-hidden="true">
            <EndIcon />
          </InputAdornment>
        )),
      style: this.props.InputProps?.style,
      ref: this.props.InputProps?.ref,
    }

    if (other.variant !== 'outlined') inputProps.disableUnderline = true

    if (other.type === 'date') other.required = true

    // Surface required state to assistive tech on the native input (WCAG 3.3.2).
    if (other.required) {
      other.inputProps = { 'aria-required': 'true', ...(other.inputProps || {}) }
    }

    // If the caller didn't supply an id, fall back to the auto-generated one so
    // MUI pairs the label's htmlFor with the input's id (axe `label` rule).
    const resolvedId = other.id || this.autoId
    return (
      <TextField
        variant="filled"
        id={resolvedId}
        {...other}
        margin="dense"
        fullWidth={true}
        onFocus={this.shrinkLabel}
        onBlur={this.unShrinkLabel}
        className={classNames}
        // Merge any caller-provided InputLabelProps so base components can opt in.
        InputLabelProps={{ ...inputLabelProps, shrink, classes }}
        InputProps={inputProps}
      />
    )
  }
}

export default withStyles(styles)(YumInput)
